import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DisputeService } from '../../services/disputes';
import { Dispute } from '../../types';
import { safeArray } from '../../utils/money';

// 异步actions
export const fetchDisputes = createAsyncThunk(
  'dispute/fetchDisputes',
  async (params: { page?: number; limit?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const response = await DisputeService.listDisputes(
        params.page,
        params.limit,
        params.filters
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取争议列表失败');
    }
  }
);

export const fetchDisputeDetail = createAsyncThunk(
  'dispute/fetchDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await DisputeService.getDispute(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取争议详情失败');
    }
  }
);

export const submitDisputeEvidence = createAsyncThunk(
  'dispute/submitEvidence',
  async (params: { id: string; evidence: any }, { rejectWithValue }) => {
    try {
      await DisputeService.submitEvidence(params.id, params.evidence);
      return params.id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '提交证据失败');
    }
  }
);

export const acceptDispute = createAsyncThunk(
  'dispute/accept',
  async (id: string, { rejectWithValue }) => {
    try {
      await DisputeService.acceptDispute(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '接受争议失败');
    }
  }
);

export const challengeDispute = createAsyncThunk(
  'dispute/challenge',
  async (id: string, { rejectWithValue }) => {
    try {
      await DisputeService.challengeDispute(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '挑战争议失败');
    }
  }
);

interface DisputeState {
  disputes: {
    data: Dispute[];
    meta: any;
    loading: boolean;
  };
  currentDispute: Dispute | null;
  evidence: any;
  submitting: boolean;
  error: string | null;
}

const initialState: DisputeState = {
  disputes: {
    data: [],
    meta: null,
    loading: false,
  },
  currentDispute: null,
  evidence: null,
  submitting: false,
  error: null,
};

const disputeSlice = createSlice({
  name: 'dispute',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentDispute: (state) => {
      state.currentDispute = null;
      state.evidence = null;
    },
  },
  extraReducers: (builder) => {
    // 获取争议列表
    builder
      .addCase(fetchDisputes.pending, (state) => {
        state.disputes.loading = true;
        state.error = null;
      })
      .addCase(fetchDisputes.fulfilled, (state, action) => {
        state.disputes.loading = false;
        state.disputes.data = safeArray<Dispute>(action.payload.data);
        state.disputes.meta = action.payload.meta;
      })
      .addCase(fetchDisputes.rejected, (state, action) => {
        state.disputes.loading = false;
        state.error = action.payload as string;
      });

    // 获取争议详情
    builder
      .addCase(fetchDisputeDetail.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchDisputeDetail.fulfilled, (state, action) => {
        state.currentDispute = action.payload;
      })
      .addCase(fetchDisputeDetail.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // 提交证据
    builder
      .addCase(submitDisputeEvidence.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(submitDisputeEvidence.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(submitDisputeEvidence.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });

    // 接受争议
    builder
      .addCase(acceptDispute.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(acceptDispute.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.disputes.data.findIndex(d => d.id === action.payload);
        if (index !== -1) {
          state.disputes.data[index].status = 'CLOSED' as any;
        }
      })
      .addCase(acceptDispute.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });

    // 挑战争议
    builder
      .addCase(challengeDispute.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(challengeDispute.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.disputes.data.findIndex(d => d.id === action.payload);
        if (index !== -1) {
          state.disputes.data[index].status = 'UNDER_REVIEW' as any;
        }
      })
      .addCase(challengeDispute.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentDispute } = disputeSlice.actions;
export default disputeSlice.reducer;
